import type { FooterBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

/**
 * Pure site footer. Supports a simple flat link row OR rich multi-column link
 * groups, plus optional social links and a fine-print disclaimer (MLS/IDX, Equal
 * Housing). All sections are optional — a bare companyName still renders cleanly.
 */
export function FooterBlock(props: FooterBlockProps) {
  const { companyName, tagline, links, columns, socialLinks, disclaimer, phone, email, address, navLinks, legalPages } = props;
  const year = new Date().getFullYear();
  // Text defaults to the READABLE on-color for the secondary (footer) surface — so a LIGHT brand
  // secondary (e.g. tan) gets dark text instead of the old hardcoded white (which was invisible).
  const colors = sectionColors(props, { bg: theme.secondary, text: theme.onSecondary });
  const hasColumns = Array.isArray(columns) && columns.length > 0;
  const hasFlatLinks = Array.isArray(links) && links.length > 0;
  const hasSocial = Array.isArray(socialLinks) && socialLinks.length > 0;

  // Every site shows its legal pages in the footer — non-negotiable for a lead-gen real estate
  // site. Guaranteed here regardless of the template's footer style (columns vs flat links) or
  // whether a build added them. Skip one only if the footer already links to it, so it never
  // appears twice.
  const normHref = (h: string): string => h.replace(/^\/+|\/+$/g, '').toLowerCase();
  const alreadyLinked = new Set<string>([
    ...(links ?? []).map((l) => normHref(l.href)),
    ...(columns ?? []).flatMap((c) => (c.links ?? []).map((l) => normHref(l.href))),
    ...(navLinks ?? []).map((l) => normHref(l.href)),
  ]);
  // `legalPages` comes from the platform catalogue when the host passes it, so a state document
  // an agent enables appears here without a deploy. Falls back to the two every site has always
  // linked — a footer must never lose its legal links because a lookup failed.
  const legalLinks = (legalPages?.length
    ? legalPages
    : [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Use', href: '/terms' },
      ]
  ).filter((l) => !alreadyLinked.has(normHref(l.href)));
  // Footer-designated menu items (nav location:'footer') render in the bottom utility row,
  // alongside the guaranteed legal links — deduped above so nothing appears twice.
  const bottomLinks = [...(navLinks ?? []), ...legalLinks];

  return (
    <footer className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-8 md:flex-row md:justify-between">
          {/* Brand block */}
          <div className="md:max-w-xs">
            {/* The brokerage name, and the one piece of a footer an AI edit must never quietly
                rewrite — "keep my brokerage information" is an explicit instruction in half the
                QA prompts. Tagged so tests can assert it survived. */}
            <div data-brokerage className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
              {companyName}
            </div>
            {tagline ? <p className="mt-1 text-sm opacity-60">{tagline}</p> : null}
            {address || phone || email ? (
              <div className="mt-3 space-y-0.5 text-sm opacity-70">
                {address ? <p>{address}</p> : null}
                {phone ? (
                  <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="block hover:opacity-100">
                    {phone}
                  </a>
                ) : null}
                {email ? (
                  <a href={`mailto:${email}`} className="block hover:opacity-100">
                    {email}
                  </a>
                ) : null}
              </div>
            ) : null}
            {hasSocial ? (
              <nav className="mt-4 flex flex-wrap gap-3">
                {socialLinks!.map((s, i) => (
                  <a
                    key={`${s.href}-${i}`}
                    href={s.href}
                    className="text-sm opacity-70 hover:opacity-100"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
            ) : null}
          </div>

          {/* Link columns (rich) or a single flat link row */}
          {hasColumns ? (
            <div className="grid flex-1 grid-cols-2 gap-8 sm:grid-cols-3 md:max-w-2xl lg:grid-cols-4">
              {columns!.map((col, ci) => (
                <div key={`${col.heading}-${ci}`}>
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide opacity-80">
                    {col.heading}
                  </div>
                  <ul className="space-y-2">
                    {(col.links ?? []).map((l, li) => (
                      <li key={`${l.href}-${li}`}>
                        <a href={l.href} className="text-sm opacity-70 hover:opacity-100">
                          {l.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : hasFlatLinks ? (
            <nav className="flex flex-wrap gap-4 md:items-start">
              {links!.map((l, i) => (
                <a key={`${l.href}-${i}`} href={l.href} className="text-sm opacity-70 hover:opacity-100">
                  {l.label}
                </a>
              ))}
            </nav>
          ) : null}
        </div>

        <div className="mt-10 border-t border-current/15 pt-6 text-xs opacity-50">
          {bottomLinks.length > 0 ? (
            <nav className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
              {bottomLinks.map((l, i) => (
                <a key={`${l.href}-${i}`} href={l.href} className="hover:opacity-100">
                  {l.label}
                </a>
              ))}
            </nav>
          ) : null}
          <p>
            © {year} {companyName}. All rights reserved.
          </p>
          {disclaimer ? <p className="mt-2 leading-relaxed">{disclaimer}</p> : null}
        </div>
      </div>
    </footer>
  );
}
